import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SiteHeader } from '../components/SiteLayout';
import { HaleWordmark } from '../components/HaleBrand';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  ShieldCheck, CheckCircle, Warning, WarningCircle, X, FloppyDisk,
  ClockCounterClockwise, FileText,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =========================================================================
 *  Hale Verified Console — /assessor/hale-verified
 *
 *  Independent of the Reviewed console. Per-SKU 4-module documentation form
 *  (Supplier · Packaging Sustainability · Transparency · Brand Governance)
 *  with live scoring + eligibility verdict + finalisation lock.
 * =======================================================================*/

const VERDICT_STYLE = {
  verified_eligible:     { tone: 'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]', Icon: CheckCircle },
  documentation_pending: { tone: 'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]', Icon: Warning },
  missing_critical_docs: { tone: 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]', Icon: WarningCircle },
  incomplete:            { tone: 'bg-gray-100 text-[#4B5563] border-gray-300', Icon: WarningCircle },
};

function VerdictBanner({ eligibility }) {
  if (!eligibility) return null;
  const m = VERDICT_STYLE[eligibility.verdict] || VERDICT_STYLE.incomplete;
  return (
    <div className={`border-l-4 ${m.tone} p-3 rounded mb-3`} data-testid="verified-eligibility-banner">
      <div className="flex items-center gap-2">
        <m.Icon size={16} weight="fill" />
        <p className="text-sm font-bold">{eligibility.label}</p>
      </div>
      <p className="text-[11px] mt-1 tabular-nums">
        Weighted score: <strong>{eligibility.weighted_score_pct}%</strong> · Documentation: <strong>{eligibility.filled}/{eligibility.total}</strong> · Eligible: <strong>{eligibility.badge_eligible ? 'YES' : 'NO'}</strong>
      </p>
      {eligibility.missing_critical?.length > 0 && (
        <p className="text-[11px] mt-1">
          Missing critical: {eligibility.missing_critical.map(m => m.label).join(' · ')}
        </p>
      )}
    </div>
  );
}

function ModuleCard({ moduleKey, label, criteria, values, scores, onChange }) {
  return (
    <div className="card-flat p-4 bg-white" data-testid={`verified-module-${moduleKey}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-[#111827]">{label}</h3>
        {scores && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-[#F3F4F6] text-[#4B5563] tabular-nums">
            {scores.filled}/{scores.total} · {scores.weighted_score}%
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        {criteria.map(c => {
          const checked = !!values[c.key];
          return (
            <label key={c.key} className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${checked ? 'border-[#E63946] bg-[#F5F3FF]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-testid={`verified-${moduleKey}-${c.key}`}>
              <input type="checkbox" checked={checked} onChange={(e) => onChange(c.key, e.target.checked)} />
              <span className="text-[12px] flex-1">{c.label}</span>
              {c.critical && <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">critical</span>}
              <span className="text-[10px] text-[#6B7280] tabular-nums">w{c.weight}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function HaleVerifiedPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSku = searchParams.get('sku') || '';

  const [criteria, setCriteria] = useState(null);
  const [skuId, setSkuId] = useState(initialSku);
  const [data, setData] = useState(null);
  const [values, setValues] = useState({ supplier: {}, packaging_sust: {}, transparency: {}, governance: {} });
  const [missing, setMissing] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finalising, setFinalising] = useState(false);
  const [err, setErr] = useState('');

  // Queue for picker
  const [queue, setQueue] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');

  // Bootstrap criteria + queue
  useEffect(() => {
    (async () => {
      try {
        const [c, q] = await Promise.all([
          axios.get(`${API_URL}/api/hale/verified/criteria`, { withCredentials: true }),
          axios.get(`${API_URL}/api/hale/verified/queue?limit=50`, { withCredentials: true }),
        ]);
        setCriteria(c.data.modules);
        setQueue(q.data.items || []);
      } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    })();
  }, []);

  const loadSku = useCallback(async (id) => {
    if (!id) return;
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API_URL}/api/hale/verified/skus/${id}`, { withCredentials: true });
      setData(r.data);
      setValues({
        supplier: r.data.modules?.supplier || {},
        packaging_sust: r.data.modules?.packaging_sust || {},
        transparency: r.data.modules?.transparency || {},
        governance: r.data.modules?.governance || {},
      });
      setMissing((r.data.missing_documents || []).join('\n'));
      setNotes(r.data.notes || '');
    } catch (e) { setErr(e?.response?.data?.detail || 'SKU not found'); setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (skuId) loadSku(skuId); }, [skuId, loadSku]);

  const toggle = (mod, key, val) => setValues(v => ({ ...v, [mod]: { ...v[mod], [key]: val } }));

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const r = await axios.post(`${API_URL}/api/hale/verified/skus/${skuId}`, {
        modules: values,
        missing_documents: missing.split('\n').map(s => s.trim()).filter(Boolean),
        notes,
      }, { withCredentials: true });
      setData(r.data);
      // refresh queue
      const q = await axios.get(`${API_URL}/api/hale/verified/queue?limit=50`, { withCredentials: true });
      setQueue(q.data.items || []);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const finalise = async () => {
    setFinalising(true); setErr('');
    try {
      const r = await axios.post(`${API_URL}/api/hale/verified/skus/${skuId}/finalise`, {}, { withCredentials: true });
      setData(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setFinalising(false); }
  };

  const filteredQueue = statusFilter ? queue.filter(q => q.status === statusFilter) : queue;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <HaleWordmark size="md" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0B0B0B] bg-[#FFE5E7] border border-[#E63946]/40 px-2 py-0.5 rounded">Verified Console · Phase H2</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={22} className="text-[#E63946]" weight="duotone" />
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">Hale · Verified Console</h1>
          </div>
          <p className="text-sm text-[#4B5563]">
            Documentation completeness, supply-chain readiness, packaging sustainability, and brand governance — separate workflow from Reviewed.
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Link to="/assessor"><Button variant="outline" className="h-8 text-xs rounded">← Assessor Console</Button></Link>
            <Link to="/assessor/hale"><Button variant="outline" className="h-8 text-xs rounded" data-testid="verified-to-reviewed">Hale Reviewed Console →</Button></Link>
            <Link to="/assessor/supplier-specs"><Button variant="outline" className="h-8 text-xs rounded" data-testid="verified-to-supplier-specs">Supplier Specs →</Button></Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Queue picker */}
          <div className="lg:col-span-2 card-flat bg-white">
            <div className="p-3 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-[#111827]">Queue ({queue.length})</h3>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-7 text-xs border border-gray-300 rounded px-1 bg-white" data-testid="verified-status-filter">
                  <option value="">All</option>
                  <option value="verified">Verified</option>
                  <option value="documentation_pending">Pending</option>
                  <option value="incomplete">Incomplete</option>
                </select>
              </div>
            </div>
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {filteredQueue.length === 0 ? <p className="text-sm text-[#4B5563] py-8 text-center">No Verified reviews yet.<br/>Pick a SKU from the Reviewed console to start.</p> :
                filteredQueue.map(q => (
                  <button key={q.id} onClick={() => navigate(`/assessor/hale-verified?sku=${q.sku_id}`)} className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 ${q.sku_id === skuId ? 'bg-[#F5F3FF]' : ''}`} data-testid={`verified-queue-row-${q.sku_id}`}>
                    <p className="text-sm font-semibold text-[#111827] truncate">{q.product_name || '—'}</p>
                    <p className="text-[11px] text-[#4B5563] truncate">{q.brand?.name || '—'} · {q.brand?.tier || ''}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${VERDICT_STYLE[q.eligibility?.verdict || 'incomplete']?.tone || 'bg-gray-100 border-gray-300'}`}>{q.eligibility?.verdict || q.status}</span>
                      <span className="text-[10px] text-[#4B5563] tabular-nums ml-auto">{q.eligibility?.weighted_score_pct ?? 0}%</span>
                    </div>
                  </button>
                ))
              }
            </div>
          </div>

          {/* Editor */}
          <div className="lg:col-span-3 space-y-3">
            <div className="card-flat p-3 bg-white flex items-center gap-2">
              <Input value={skuId} onChange={(e) => setSkuId(e.target.value)} placeholder="Paste a Hale SKU id…" className="h-9 text-sm" data-testid="verified-sku-input" />
              <Button onClick={() => loadSku(skuId)} className="h-9 rounded bg-[#0B1426] text-white text-sm" data-testid="verified-load-sku">Load</Button>
            </div>

            {err && <div className="card-flat p-3 bg-[#FDE8E8] border border-[#F8B4B4] text-[#9B1C1C] text-sm" data-testid="verified-error">{err}</div>}

            {loading && <p className="text-sm text-[#4B5563] py-6 text-center">Loading…</p>}

            {data && (
              <>
                <div className="card-flat p-4 bg-white" data-testid="verified-sku-header">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">SKU</p>
                  <h3 className="text-base font-bold text-[#111827]">{data.product_name || data.sku_id}</h3>
                  <p className="text-[11px] text-[#4B5563]">Status: <strong>{data.status}</strong></p>
                </div>

                {data.eligibility && <VerdictBanner eligibility={data.eligibility} />}

                {criteria && Object.entries(criteria).map(([k, m]) => (
                  <ModuleCard
                    key={k} moduleKey={k} label={m.label} criteria={m.criteria}
                    values={values[k] || {}} scores={data.module_scores?.[k]}
                    onChange={(key, val) => toggle(k, key, val)}
                  />
                ))}

                <div className="card-flat p-4 bg-white">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Missing documents (one per line)</Label>
                  <Textarea value={missing} onChange={(e) => setMissing(e.target.value)} rows={2} className="text-[12px] mt-1 mb-3" placeholder="e.g. Updated CoA from Acme Chem (2026 batch)" data-testid="verified-missing-docs" />
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Reviewer notes</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="text-[12px] mt-1" data-testid="verified-notes" />
                </div>

                <div className="flex gap-2">
                  <Button onClick={save} disabled={saving} className="h-9 rounded bg-[#E63946] hover:bg-[#C9192C] text-white text-sm font-semibold" data-testid="verified-save">
                    <FloppyDisk size={13} weight="bold" className="mr-1.5" /> {saving ? 'Saving…' : 'Save & score'}
                  </Button>
                  {data.exists && !data.finalised_at && (
                    <Button onClick={finalise} disabled={finalising} className="h-9 rounded bg-[#0B1426] hover:bg-[#1B2538] text-white text-sm font-semibold" data-testid="verified-finalise">
                      <CheckCircle size={13} weight="fill" className="mr-1.5" /> {finalising ? 'Finalising…' : 'Finalise verdict'}
                    </Button>
                  )}
                  {data.finalised_at && (
                    <span className="text-[11px] text-[#03543F] bg-[#DEF7EC] border border-[#31C48D] rounded px-2 py-1 self-center" data-testid="verified-finalised-chip">
                      Finalised by {data.finalised_by} · {new Date(data.finalised_at).toLocaleString()}
                    </span>
                  )}
                </div>

                {data.audit?.length > 0 && (
                  <div className="card-flat p-4 bg-white">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-2 flex items-center gap-1"><ClockCounterClockwise size={11} /> Audit ({data.audit.length})</p>
                    <div className="space-y-1.5">
                      {data.audit.slice().reverse().map((a, i) => (
                        <div key={i} className="text-[11px] text-[#4B5563] border-b border-gray-100 pb-1.5">
                          <span className="font-mono text-[10px] bg-gray-100 px-1 rounded">{a.action}</span>
                          <span className="ml-2">{a.by}</span>
                          <span className="ml-2 text-[#9CA3AF]">· {new Date(a.at).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {!skuId && !data && !loading && (
              <div className="card-flat p-6 bg-white text-center text-[#4B5563] text-sm flex flex-col items-center gap-2">
                <FileText size={32} weight="duotone" className="text-[#9CA3AF]" />
                Paste a SKU id above, or pick from the queue.
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
